import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from '@tanstack/react-query';
import { create } from "zustand";
import { Button, TextField, Container, Grid, Box, CircularProgress, Pagination, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import Documentos from '../DocumentosPdf/Documentos';
import CardItemVerbetes from "./CardItemVerbetes";
import "./CardsVerbetes.css";

const useCardsStore = create((set) => ({
  search: "",
  searchResults: [],
  setSearch: (search) => set({ search }),
  setSearchResults: (results) => set({ searchResults: results }),
}));

const fetchVerbetes = async () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    const response = await fetch(`${apiUrl}/verbetes/`);
    if (!response.ok) {
      throw new Error("Erro ao buscar dados");
    }
    return await response.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const DOCUMENTO_AVULSO_TAG = "__documento_avulso__";

const isDocumentoAvulso = (item) =>
  String(item?.palavrachave || "").toLowerCase().includes(DOCUMENTO_AVULSO_TAG);

const CardsVerbetes = () => {
  const location = useLocation();
  const [searchType, setSearchType] = useState("verbetes");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { search, setSearch, searchResults, setSearchResults } = useCardsStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ["verbetes"],
    queryFn: fetchVerbetes,
  });

  // Se `data` estiver indefinido, evita erro
  const totalItems = data ? data.filter((item) => !isDocumentoAvulso(item)).length : 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab === "documentos") {
      setSearchType("documentos");
    } else if (tab === "verbetes") {
      setSearchType("verbetes");
    }
  }, [location.search]);

  useEffect(() => {
    if (!search || !data) return; // Evita buscar quando não houver pesquisa ou dados
    const filtered = data
      .filter((item) => !isDocumentoAvulso(item))
      .filter((item) =>
        ["titulo", "autor", "palavrachave", "textoPortugues"].some(
          (key) => item[key]?.toLowerCase().includes(search.toLowerCase())
        )
      );
    setSearchResults(filtered);
  }, [search, data, setSearchResults]);

  // Paginação
  const paginatedData = useMemo(() => {
    const source = search ? searchResults : (data || []).filter((item) => !isDocumentoAvulso(item));
    return source.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [search, searchResults, data, currentPage]);

  return (
    <Container className="cards_BD">
      <Box display="flex" justifyContent="center" mb={3}>
        <Button
          variant={searchType === "verbetes" ? "contained" : "outlined"}
          onClick={() => setSearchType("verbetes")}
          sx={{
            ml: 2,
            backgroundColor: searchType === "verbetes" ? "#3f51b5" : "#fff",
            color: searchType === "verbetes" ? "#fff" : "#3f51b5",
            borderRadius: "30px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: searchType === "verbetes" ? "#303f9f" : "#f1f1f1",
            },
            padding: "8px 16px",
            transition: "all 0.3s ease",
          }}
        >
          Buscar em Verbetes
        </Button>

        <Button
          variant={searchType === "documentos" ? "contained" : "outlined"}
          onClick={() => setSearchType("documentos")}
          sx={{
            ml: 2,
            backgroundColor: searchType === "documentos" ? "#3f51b5" : "#fff",
            color: searchType === "documentos" ? "#fff" : "#3f51b5",
            borderRadius: "30px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: searchType === "documentos" ? "#303f9f" : "#f1f1f1",
            },
            padding: "8px 16px",
            transition: "all 0.3s ease",
          }}
        >
          Buscar em Documentos
        </Button>
      </Box>

      {searchType === "verbetes" ? (
        <>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar verbetes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
            sx={{
              backgroundColor: "#fff",
              borderRadius: "30px",
              padding: "10px 20px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "30px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              },
              "& .MuiInputBase-input": {
                padding: "10px",
                fontSize: "16px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3f51b5",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#3f51b5",
              },
              transition: "all 0.3s ease",
            }}
          />

          <Box mt={3}>
            {isLoading ? (
              <CircularProgress color="warning" />
            ) : error ? (
              <p>Erro ao carregar os verbetes.</p>
            ) : (
              <>
                <Grid container spacing={2} justifyContent="center">
                  {paginatedData.map((item) => (
                    <Grid item xs={12} sm={6} md={6} key={item._id} sx={{ display: 'flex' }}>
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ width: '100%' }}>
                        <CardItemVerbetes
                          titulo={item.titulo}
                          textoPortugues={item.textoPortugues}
                          autor={item.autor}
                          imgcard={item.imgcard}
                          linkid={item._id}
                        />
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>

                {/* PAGINAÇÃO MUI */}
                <Box mt={3} display="flex" justifyContent="center">
                  <Stack spacing={2}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, value) => setCurrentPage(value)}
                      color="primary"
                    />
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </>
      ) : (
        <Documentos />
      )}
    </Container>
  );
};

export default CardsVerbetes;
